import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { C_BTN } from "../ConfigListShell/buttonMetrics";

/**
 * SIMS-8614 — the full-page configuration editor chrome.
 *
 * Spec — `Payers Pattern v2.dc.html`:
 *   header bar    L385-412  ONE bar: --surface-0, bottom divider, 14px 24px padding. Breadcrumb,
 *                           title, status pill and the actions all live inside it — the breadcrumb
 *                           is NOT a separate block on the page background above the card.
 *   breadcrumb    L387-393  Program setup / Payers / {current}, 12px, stacked 2px above the title
 *   dirty pill    L396-398  12px/500, warning wash, 999px, `3px 10px`
 *   saving pill   L399-401  same box, neutral
 *   Cancel/Save   L411-412  Save disabled while saving
 *   fail banner   L417-427  above the form, states the reason
 *   discard modal L547-558  440px — "Discard unsaved changes?" / Keep editing / Discard changes
 *   section cards L409/456  --surface-0, 1px --border-default, 12px radius, 20px 24px 24px
 *
 * Chrome only — the fields are `children`. SIMS-8607 (pattern tester), SIMS-8653 (mode notice) and
 * SIMS-8634 (categories) each add fields with their own validation to this same editor, so the
 * shell must not own the form.
 *
 * Rendered in place of the list rather than at its own route (developer decision 2026-08-28):
 * all four ACs are satisfied without touching shared routing, and no AC asks for a deep link to
 * an editor. A real route can follow as its own change once the pattern is proven.
 *
 * The dirty pill uses the theme's warning palette, as `.c-pill--warning` does in
 * `card-system.css:276`. NOT `--warning-ink`, which the design references but which exists in
 * neither the design system nor this codebase (OQ-13) — same call as the status pill in SIMS-8594.
 */

export interface EditorBreadcrumb {
	label: string;
	/** Omit for the trailing (current) crumb. */
	onClick?: () => void;
}

/** `.c-btn` metrics, shared by every button in the editor chrome. */
// Shared `.c-btn` metrics — see ConfigListShell/buttonMetrics.ts. Previously duplicated here,
// which is how the line-height drifted from the delete dialog's copy.
const BTN = C_BTN;

export interface ConfigEditorShellProps {
	breadcrumbs: EditorBreadcrumb[];
	title: string;
	/**
	 * Body copy for the discard prompt. Passed in because the design's wording names the entity —
	 * "This payer has changes that have not been saved..." (L571) — and a shell shared by future
	 * config editors cannot hardcode "payer".
	 */
	discardBody: string;
	isDirty: boolean;
	isSaving: boolean;
	canSave: boolean;
	saveError: string | null;
	/** Banner title, e.g. "Payer wasn't saved" (design L376). */
	failedTitle?: string;
	discardPromptOpen: boolean;
	onSave: () => void;
	onCancel: () => void;
	onBreadcrumb: () => void;
	onKeepEditing: () => void;
	onDiscard: () => void;
	/**
	 * Delete the record being edited (design L405-410). Optional: the design gates the button on
	 * `canDelete: !!editPayer && !s.isNew`, so a shell rendering a NEW record simply omits it.
	 *
	 * The handler opens the SAME confirmation the list uses — the dependency counts, the refusal
	 * and the server-side 409 all still apply. This is another way in, not a second delete path.
	 */
	onDelete?: () => void;
	children: ReactNode;
}

const ConfigEditorShell = ({
	breadcrumbs,
	title,
	discardBody,
	isDirty,
	isSaving,
	canSave,
	saveError,
	failedTitle = "Changes weren't saved",
	discardPromptOpen,
	onSave,
	onCancel,
	onBreadcrumb,
	onKeepEditing,
	onDiscard,
	onDelete,
	children,
}: ConfigEditorShellProps) => (
	<Box
		sx={{
			// The Layout's <Main> pads 20px on every side (views/Layout/index.tsx:56). The design's
			// editor wrapper has NO padding (L384: `flex: 1; display: flex; flex-direction: column;
			// min-height: 0`), so the header bar runs edge to edge and supplies its own 14px/24px
			// gutters. Inheriting <Main>'s padding inset the bar and left it floating inside the
			// page rather than spanning it. Cancelled here — the same correction the list view
			// makes (pages/Payers/index.tsx) — so both views share one basis.
			m: "-20px",
			minHeight: "calc(100vh - 82px)",
			display: "flex",
			flexDirection: "column",
		}}
	>
		{/* ONE header bar holding breadcrumb, title, status chip and actions (design L385-412):
		    `--surface-0` with a bottom divider and 14px/24px padding. The breadcrumb sits INSIDE
		    this bar, stacked 2px above the title — not on the page background above it, which
		    left the crumb visually detached from the screen it names. */}
		<Box
			sx={{
				position: "sticky",
				top: 0,
				zIndex: 2,
				backgroundColor: "background.paper",
				borderBottom: "1px solid",
				borderColor: "divider",
				display: "flex",
				alignItems: "center",
				gap: 2,
				padding: "0.875rem 1.5rem",
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Breadcrumbs
					sx={{
						fontSize: "0.75rem",
						"& .MuiBreadcrumbs-li": { fontSize: "0.75rem" },
					}}
				>
					{breadcrumbs.map((crumb) =>
						crumb.onClick ? (
							<Link
								key={crumb.label}
								component="button"
								type="button"
								underline="hover"
								color="inherit"
								onClick={onBreadcrumb}
								sx={{ font: "inherit" }}
							>
								{crumb.label}
							</Link>
						) : (
							<Typography
								key={crumb.label}
								color="text.secondary"
								sx={{ fontSize: "0.75rem" }}
							>
								{crumb.label}
							</Typography>
						),
					)}
					<Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
						{title}
					</Typography>
				</Breadcrumbs>

				<Box
					sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "2px" }}
				>
					{/* 24px / line-height 32 (design L395) — the same h1 scale as the list header.
					    MUI's h4 is ~34px, which made the editor title bigger than the list's. */}
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", lineHeight: "2rem", m: 0 }}
					>
						{title}
					</Typography>

					{/* One slot, one message: the design has two chips here and showing both would
					    contradict itself. Saving wins — it is the more immediate truth. */}
					{isSaving ? (
						<Chip
							size="small"
							label="Saving…"
							sx={{
								height: "1.5rem",
								borderRadius: 999,
								fontSize: "0.75rem",
								fontWeight: 500,
							}}
						/>
					) : (
						isDirty && (
							<Chip
								size="small"
								label="Unsaved changes"
								color="warning"
								sx={{
									height: "1.5rem",
									borderRadius: 999,
									fontSize: "0.75rem",
									fontWeight: 500,
									lineHeight: 1,
								}}
							/>
						)
					)}
				</Box>
			</Box>

			<Box sx={{ flex: 1 }} />
			{/* Delete sits BEFORE Cancel (design L405-411) and is `.c-btn--tertiary`: transparent,
			    no border, --error-dark text. Destructive without competing with Save for
			    attention, which a filled red button next to it would. */}
			{onDelete && (
				<Button
					onClick={onDelete}
					disabled={isSaving}
					startIcon={<DeleteOutlineIcon />}
					sx={{
						...BTN,
						color: "error.dark",
						px: 1,
						"&:hover": {
							backgroundColor: (t) => alpha(t.palette.error.main, 0.06),
						},
					}}
				>
					Delete
				</Button>
			)}
			{/* `.c-btn--secondary` / `.c-btn--primary`: 40px min-height, 8px radius, UPPERCASE
			    with .04em tracking, brand outline on the secondary (card-system.css:338/344/346). */}
			<Button
				variant="outlined"
				onClick={onCancel}
				disabled={isSaving}
				sx={{ ...BTN, borderColor: "primary.main" }}
			>
				Cancel
			</Button>
			<Button variant="contained" onClick={onSave} disabled={!canSave} sx={BTN}>
				Save
			</Button>
		</Box>

		{/* Scrolling body (design L416): `flex: 1; overflow-y: auto; padding: 20px 24px 40px;
		    gap: 16px`. It owns the content gutters because the wrapper deliberately has none — that
		    is what lets the header bar above it span the full width. */}
		<Box
			sx={{
				flex: 1,
				overflowY: "auto",
				padding: "1.25rem 1.5rem 2.5rem",
				display: "flex",
				flexDirection: "column",
				gap: 2,
			}}
		>
			{/* AC3: the reason sits ABOVE the form and never replaces it — every entered value stays.
		    Design L373-381: --error-light panel, 1px --error-main, 8px radius, 14px 16px padding,
		    a 14px/600 title, the reason at 13px/18, and a "Try again" secondary button. A bare
		    MUI Alert dropped the title and the retry. */}
			{saveError !== null && (
				<Box
					role="alert"
					sx={{
						display: "flex",
						alignItems: "flex-start",
						gap: 1.5,
						backgroundColor: "error.light",
						border: "1px solid",
						borderColor: "error.main",
						borderRadius: "0.5rem",
						padding: "0.875rem 1rem",
					}}
				>
					<ErrorOutlineIcon sx={{ fontSize: "1.25rem", color: "error.dark" }} />
					<Box>
						<Typography
							sx={{
								fontSize: "0.875rem",
								fontWeight: 600,
								color: "error.dark",
							}}
						>
							{failedTitle}
						</Typography>
						<Typography
							sx={{
								fontSize: "0.8125rem",
								lineHeight: "1.125rem",
								color: "error.dark",
								mt: "2px",
							}}
						>
							{saveError}
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }} />
					{onSave && (
						<Button
							size="small"
							variant="outlined"
							onClick={onSave}
							sx={{ ...BTN, minHeight: "2rem", borderColor: "primary.main" }}
						>
							Try again
						</Button>
					)}
				</Box>
			)}

			{children}
		</Box>

		{/* Discard prompt (design L567-576). Deliberately specced rather than left as stock MUI:
		    440px fixed width (not maxWidth="xs" + fullWidth, which is ~444px at one breakpoint and
		    narrower below it), 12px radius, and the design's own paddings — title 20px 24px 8px,
		    body 0 24px 16px, actions 12px 24px 20px with a 12px gap. MUI's Dialog defaults add
		    their own padding on all three and a 4px radius. */}
		<Dialog
			open={discardPromptOpen}
			onClose={onKeepEditing}
			PaperProps={{
				sx: {
					width: "27.5rem",
					maxWidth: "100%",
					m: 2,
					borderRadius: "0.75rem",
				},
			}}
		>
			{/* 18px/700 (L570) — MUI's DialogTitle is 20px/500, so both differ. */}
			<DialogTitle
				sx={{
					padding: "1.25rem 1.5rem 0.5rem",
					fontSize: "1.125rem",
					fontWeight: 700,
					lineHeight: 1.5,
				}}
			>
				Discard unsaved changes?
			</DialogTitle>
			<DialogContent sx={{ padding: "0 1.5rem 1rem" }}>
				<Typography sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}>
					{discardBody}
				</Typography>
			</DialogContent>
			<DialogActions sx={{ padding: "0.75rem 1.5rem 1.25rem", gap: 1.5 }}>
				{/* Keep editing is the safe default and comes first, so a hurried Enter does not
				    destroy work. `.c-btn--secondary` is brand-outlined, not MUI's borderless text
				    button — the two choices must read as comparable weight, and a text button
				    beside a filled one does not. */}
				<Button
					variant="outlined"
					onClick={onKeepEditing}
					sx={{ ...BTN, borderColor: "primary.main" }}
				>
					Keep editing
				</Button>
				{/* `.c-btn--danger` (card-system.css:348): --error-main filled, white, UPPERCASE. */}
				<Button color="error" variant="contained" onClick={onDiscard} sx={BTN}>
					Discard changes
				</Button>
			</DialogActions>
		</Dialog>
	</Box>
);

export default ConfigEditorShell;
